import Cookies from 'js-cookie';
import {getCrossSubdomainCookieDomain} from '../helpers/cookieHelpers';

const GOOGLE_CLICK_ID = 'gclid',
      FBCLID = 'fbclid',
      FBCLID_TIMESTAMP = 'fbclid_timestamp',
      // Landing click ids the server falls back on (migrationContextService);
      // the 90-day expiry is its click bound, the fbclid pair carries its time.
      // fbclid, fbclid_timestamp, gbraid and wbraid must be classified as
      // Marketing in Cookiebot.
      LANDING_CLICK_IDS = [FBCLID, 'gbraid', 'wbraid'],
      LANDING_CLICK_ID_PATTERN = /^[\w-]{1,512}$/u,
      LANDING_CLICK_COOKIE_OPTIONS = {'expires': 90},
      COOKIEBOT_COOKIE = 'CookieConsent',
      COOKIEBOT_NOT_DECLINED_VALUES = new Set(['-1', '-2']), // out of region, no response yet
      COOKIEBOT_MARKETING_CONSENT_PATTERN = /[{,]marketing:true[,}]/u;

/**
 * Service for handling Google Click ID tracking for conversion attribution : SCP-3112
 */
class GoogleClickTrackingService {
	init() {
		// Pages that turn tracking off (the payment page, CT-376) store no click ids.
		if (window.globalParams?.trackingAllowed === false) {
			return;
		}

		this.#initGoogleClickTracking();
		this.#storeLandingClickIds();
	}

	/**
	 * @private
	 */
	#initGoogleClickTracking() {
		const currentGoogleClickId = this.#extractGoogleClickIdFromUrl();

		if (currentGoogleClickId) {
			this.#storeGoogleClickId(currentGoogleClickId);
		}
	}

	/**
	 * @private
	 */
	#storeLandingClickIds() {
		if (this.#hasDeclinedMarketing()) {
			return;
		}

		const urlParams = new URLSearchParams(window.location.search);

		LANDING_CLICK_IDS.forEach((name) => {
			const id      = urlParams.get(name),
			      isKnown = Cookies.get(name) === id &&
				      (name !== FBCLID || Cookies.get(FBCLID_TIMESTAMP) !== undefined);

			// A reload of the landing URL keeps the first-seen click time and expiry.
			if (!LANDING_CLICK_ID_PATTERN.test(id ?? '') || isKnown) {
				return;
			}

			this.#setCookie(name, id, LANDING_CLICK_COOKIE_OPTIONS);

			if (name === FBCLID) {
				this.#setCookie(FBCLID_TIMESTAMP, String(Date.now()), LANDING_CLICK_COOKIE_OPTIONS);
			}
		});
	}

	/**
	 * A marketing decline in Cookiebot's CookieConsent cookie, by the server's
	 * rule (migrationContextService): on a label with a CMP, a decision other
	 * than the -1 (out of region) and -2 (no response) sentinels without
	 * marketing:true. No cookie is no decision yet and still writes: the
	 * server reads these only once marketing is allowed. js-cookie
	 * percent-decodes the value, as @fastify/cookie does.
	 * @returns {boolean}
	 * @private
	 */
	#hasDeclinedMarketing() {
		const cmp     = window.globalParams?.cookiebot,
		      consent = Cookies.get(COOKIEBOT_COOKIE);

		return cmp !== false && cmp !== null && consent !== undefined &&
			!COOKIEBOT_NOT_DECLINED_VALUES.has(consent) && !COOKIEBOT_MARKETING_CONSENT_PATTERN.test(consent);
	}
	
	/**
	 * @returns {string|null}
	 * @private
	 */
	#extractGoogleClickIdFromUrl() {
		const urlParams = new URLSearchParams(window.location.search);

		return urlParams.get(GOOGLE_CLICK_ID);
	}

	/**
	 * @param {string} googleClickId
	 * @private
	 */
	#storeGoogleClickId(googleClickId) {
		this.#setCookie(GOOGLE_CLICK_ID, googleClickId);
	}

	/**
	 * @param {string} name
	 * @param {string} value
	 * @param {!Object} [extraOptions]
	 * @private
	 */
	#setCookie(name, value, extraOptions = {}) {
		Cookies.set(
			name,
			value,
			{
				'path'    : '/',
				'sameSite': 'Lax',
				'domain'  : getCrossSubdomainCookieDomain(),
				...extraOptions
			}
		);
	}
}

export default new GoogleClickTrackingService();